import { useMemo } from 'react';

import {
  useCampaignPostCallMessageCreateControllerCreateMutation,
  useCampaignPostCallMessageDeleteControllerDeleteMutation,
  useCampaignPostCallMessageUpdateControllerUpdateMutation,
  useCampaignPostCallMessagesControllerListQuery,
  useCampaignStructuredOutputListControllerListQuery,
} from '@shared/services/apiService/apiService';

import {
  getCampaignAnalysisFieldNames,
  getCampaignAnalysisFields,
} from '../utils/campaign-analysis-fields';
import {
  PostCallMessageDraft,
  buildPostCallMessageCreateRequest,
  buildPostCallMessageUpdateRequest,
  createEmptyPostCallMessageDraft,
  normalizePostCallMessage,
  sortPostCallMessages,
} from '../utils/post-call-message-mapper';

export const useCampaignPostCallMessages = (campaignId?: string) => {
  const {
    data: messagesResponse,
    isLoading: isMessagesLoading,
    isFetching: isMessagesFetching,
    refetch: refetchMessages,
  } = useCampaignPostCallMessagesControllerListQuery(
    { campaignId: campaignId! },
    { skip: !campaignId, refetchOnMountOrArgChange: true },
  );

  const { data: structuredOutputs } = useCampaignStructuredOutputListControllerListQuery(
    { id: campaignId! },
    { skip: !campaignId, refetchOnMountOrArgChange: true },
  );

  const [createMessage, { isLoading: isCreating }] =
    useCampaignPostCallMessageCreateControllerCreateMutation();
  const [updateMessage, { isLoading: isUpdating }] =
    useCampaignPostCallMessageUpdateControllerUpdateMutation();
  const [deleteMessageMutation, { isLoading: isDeleting }] =
    useCampaignPostCallMessageDeleteControllerDeleteMutation();

  const messages = useMemo(
    () => sortPostCallMessages((messagesResponse ?? []).map(normalizePostCallMessage)),
    [messagesResponse],
  );

  const analysisFields = useMemo(
    () => getCampaignAnalysisFields(structuredOutputs),
    [structuredOutputs],
  );

  const analysisFieldNames = useMemo(
    () => getCampaignAnalysisFieldNames(structuredOutputs),
    [structuredOutputs],
  );

  const requireCampaignId = (): string => {
    if (!campaignId) {
      throw new Error('Campaign id is required');
    }

    return campaignId;
  };

  const saveMessage = async (draft: PostCallMessageDraft): Promise<void> => {
    const id = requireCampaignId();

    if (draft.id) {
      await updateMessage({
        campaignId: id,
        messageId: draft.id,
        campaignPostCallMessageUpdateRequestDto: buildPostCallMessageUpdateRequest(draft),
      }).unwrap();
    } else {
      await createMessage({
        campaignId: id,
        campaignPostCallMessageCreateRequestDto: buildPostCallMessageCreateRequest({
          ...draft,
          order: messages.length,
        }),
      }).unwrap();
    }

    await refetchMessages();
  };

  const deleteMessage = async (messageId: string): Promise<void> => {
    await deleteMessageMutation({ campaignId: requireCampaignId(), messageId }).unwrap();

    await refetchMessages();
  };

  const moveMessage = async (index: number, direction: -1 | 1): Promise<void> => {
    const id = requireCampaignId();
    const targetIndex = index + direction;

    if (targetIndex < 0 || targetIndex >= messages.length) {
      return;
    }

    const message = messages[index];
    const neighbour = messages[targetIndex];

    if (!message.id || !neighbour.id) {
      return;
    }

    await updateMessage({
      campaignId: id,
      messageId: message.id,
      campaignPostCallMessageUpdateRequestDto: { order: targetIndex },
    }).unwrap();

    await updateMessage({
      campaignId: id,
      messageId: neighbour.id,
      campaignPostCallMessageUpdateRequestDto: { order: index },
    }).unwrap();

    await refetchMessages();
  };

  return {
    messages,
    analysisFields,
    analysisFieldNames,
    isLoading: isMessagesLoading || isMessagesFetching,
    isSaving: isCreating || isUpdating,
    isDeleting,
    createDraft: createEmptyPostCallMessageDraft,
    saveMessage,
    deleteMessage,
    moveMessage,
    refetchMessages,
  };
};
